import styled from 'styled-components'
import { HiddenInput } from '@/components/common/HiddenInput'
import Icon from '@/components/common/Icon'
import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import { device } from '@/common/constants/device'

const BaseCheckboxLeft = styled.div`
  position: relative;

  span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 21px;
    height: 21px;
    border-radius: 8px;
    cursor: pointer;
    border: 1px solid var(--stroke-2);
    transition: border-color 0.26s ease-in-out;

    @media ${device.tablet} {
      width: 26px;
      height: 26px;
    }
  }
`

const BaseCheckboxText = styled.div`
  font-size: 16px;
  line-height: 1.5em;
  color: var(--tertiary);
  border-bottom: ${({ alert }) => alert && '2px solid var(--red)'};
`

const BaseCheckboxIcon = styled(Icon)`
  width: 13px;
  height: 13px;
  background-color: var(--white);
  opacity: 0;
  transition: opacity 0.26s ease-in-out, background-color 0.26s ease-in-out;

  @media ${device.tablet} {
    width: 16px;
    height: 16px;
  }
`

const BaseCheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  column-gap: 12px;
  cursor: pointer;
  padding-bottom: 4px;

  &.checkboxValidationClass {
    ${BaseCheckboxLeft} span {
      border: 1px solid var(--red);
      background-color: rgba(255, 0, 0, 0.1);
    }
  }

  &:hover:not(.checked) {
    ${BaseCheckboxLeft} {
      span {
        border-color: var(--purple);
        background-color: rgba(106, 103, 218, 0.1);
      }

      ${BaseCheckboxIcon} {
        background-color: var(--purple);
        opacity: 0.3;
      }
    }
  }

  ${HiddenInput}:checked {
    + span {
      background-color: var(--purple);
      border-color: transparent;

      ${BaseCheckboxIcon} {
        opacity: 1;
      }
    }
  }

  &.disabled {
    opacity: 0.7;
    pointer-events: none;
  }
`

const BaseCheckbox = ({
  name,
  checked,
  className = '',
  label,
  children,
  value,
  onChange,
  alert,
  isLineCheckbox,
  isDisabled,
  ...props
}) => {
  const [isChecked, setChecked] = useState(checked)
  const isFirstRender = useRef(true)

  const baseCheckboxClasses = classNames({
    checked: isChecked,
    line: isLineCheckbox,
    disabled: isDisabled,
  })

  const checkedCheckboxIcon = useMemo(() => {
    return isLineCheckbox ? 'minus' : 'check'
  }, [isLineCheckbox])

  const onChangeHandler = () => {
    setChecked((value) => !value)
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    onChange(isChecked)
  }, [isChecked])

  return (
    <BaseCheckboxLabel className={`${className} ${baseCheckboxClasses}`}>
      <BaseCheckboxLeft>
        <HiddenInput
          value={value}
          onChange={onChangeHandler}
          checked={checked}
          name={name}
          type="checkbox"
          {...props}
        />
        <span>
          <BaseCheckboxIcon icon={checkedCheckboxIcon} />
        </span>
      </BaseCheckboxLeft>
      <BaseCheckboxText className="base-checkbox-text" alert={alert}>
        {label}
        {children}
      </BaseCheckboxText>
    </BaseCheckboxLabel>
  )
}

export default BaseCheckbox
